import {
  CourseMaterial,
  useCoursesLoaderData,
  useProgramDetails,
} from "./coursesLoader.ts";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import {
  getMaterialStoreProducts,
  isMaterialInStoreCart,
} from "./common/materialStoreProductUtils.ts";
import { Variant } from "../../../../common/api/decimal/methods/product.ts";

export default function useTermStoreMetrics() {
  const { courses } = useCoursesLoaderData();
  const layoutData = useLayoutData();
  const { scope, participating } = useProgramDetails();

  // Only calculate metrics for courses where the user is a learner
  const learnerMaterials = courses.reduce(
    (acc, { userRole, courseMaterials }) => {
      if (userRole === "learner" && courseMaterials) {
        acc.push(...courseMaterials);
      }
      return acc;
    },
    [] as CourseMaterial[]
  );

  const requiredToPurchaseBase = learnerMaterials.filter(
    (material) =>
      material.requiredCode === "required" &&
      (material.materialStatus === "not_included" ||
        material.materialStatus === "opted_out" ||
        material.materialStatus === null)
  );

  const forPurchaseMaterials = requiredToPurchaseBase.filter(
    (material) => material.materialStatus !== "opted_out"
  );

  // note in EA opted out, in program materials are considered included
  // but flagged excluded in an attempt to lure the user back into the program
  const requiredToPurchaseActual = requiredToPurchaseBase.filter(
    (material) =>
      !(
        material.materialStatus === "opted_out" &&
        scope === "ea_only" &&
        !participating
      )
  );

  // if no product then they can't purchase it, if its in the cart then we don't need to show it
  // todo already have a license
  const requiredToPurchase = requiredToPurchaseActual.filter(
    (material) =>
      material.product &&
      !isMaterialInStoreCart(material, layoutData.storeCart?.lineItems)
  );

  const requiredToPurchaseTotalCost = requiredToPurchase.reduce((acc, cur) => {
    const { defaultVariant } = getMaterialStoreProducts(cur);
    const priceStr = defaultVariant?.studentPrice;
    if (priceStr) {
      const price = parseFloat(priceStr);
      if (!isNaN(price)) {
        return acc + price;
      }
    }
    return acc;
  }, 0);

  const requiredToPurchaseInCart = requiredToPurchaseBase.filter((material) =>
    isMaterialInStoreCart(material, layoutData.storeCart?.lineItems)
  );

  const purchaseCurrency =
    requiredToPurchase
      .flatMap((material) => material.product ?? [])
      .flatMap((product) => (product.variants as Variant[]) ?? [])
      .find((variant) => variant.studentCurrency)?.studentCurrency ?? "USD";

  const requiredToPurchaseDefaultVariantIds = requiredToPurchase
    .map((material) => {
      const { defaultVariant } = getMaterialStoreProducts(material);
      return defaultVariant?.id;
    })
    .filter<number>((id): id is number => id !== undefined);

  // Marketplace listings and adopted supplies need a specific variant chosen,
  // so they can't go into the cart in bulk
  const materialsBlockingAddAll = requiredToPurchase.filter((material) => {
    const { defaultVariant, isAdoptedSupply } =
      getMaterialStoreProducts(material);
    return defaultVariant?.condition === "marketplace" || isAdoptedSupply;
  });

  const canAddAllToCart = materialsBlockingAddAll.length === 0;

  return {
    forPurchaseCount: forPurchaseMaterials.length,
    requiredToPurchase,
    requiredToPurchaseCount: requiredToPurchase.length,
    requiredToPurchaseInCartCount: requiredToPurchaseInCart.length,
    requiredToPurchaseDefaultVariantIds,
    requiredToPurchaseBaseCount: requiredToPurchaseBase.length,
    needsPurchase: !!(
      requiredToPurchaseInCart.length !== requiredToPurchase.length &&
      requiredToPurchase.length
    ),
    requiredToPurchaseTotalCost,
    purchaseCurrency,
    alreadyPurchasedCount: 0, // todo add this when available
    canAddAllToCart,
  };
}
